/**
 * QR return-to helper.
 *
 * When an UNauthenticated user opens a scaffold QR deep-link (`/qr/scaffold/:id`)
 * the auth guard sends them to /login. We stash that target here so the OTP
 * success flow can return them to the scaffold afterwards instead of dumping
 * them on /dashboard.
 *
 * DELIBERATELY scoped to `/qr/` paths only — it never affects the normal
 * login → dashboard flow (a missing/non-QR value always falls back to
 * /dashboard at the call sites). Cleared after a successful login and capped by
 * a short TTL so a stale value can't hijack a later normal login.
 */
const KEY = 'fieldtrix:qrReturnTo';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

function isQrPath(path: unknown): path is string {
  // Internal path under /qr/ only — guards against open-redirects + scope creep.
  return (
    typeof path === 'string' &&
    path.startsWith('/qr/') &&
    !path.startsWith('//')
  );
}

/** Remember a QR deep-link the user was bounced to login from. No-op for any
 *  non-QR path, so the standard login flow is untouched. */
export function setQrReturnTo(path: string | undefined | null): void {
  try {
    if (!isQrPath(path)) return;
    sessionStorage.setItem(KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/** Read the remembered QR path WITHOUT removing it (both post-login redirects
 *  read it, so consume-on-read would let the second one fall back to dashboard
 *  and override the first). Returns null if absent, non-QR, or stale. */
export function peekQrReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { path?: unknown; ts?: unknown };
    if (!isQrPath(parsed.path)) return null;
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > TTL_MS) {
      return null;
    }
    return parsed.path;
  } catch {
    return null;
  }
}

export function clearQrReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
